import { VASTLoader } from '@lib/iab/iab-vast-loader/lib/loader';
import {
    VAST,
    MediaFile,
    AbstractAd,
    TrackingEvents,
    VideoClicks,
    Impression,
    Click,
    InLine,
    TrackingEvent,
} from '@lib/iab/iab-vast-model';
import videojs from 'video.js';
import Player from 'video.js/dist/types/player';

import { DEFAULT_TIMEOUT, PROGRESS_THRESHOLDS } from './constants';
import {
    AdData,
    ProgressEventName,
    ProgressEventNames,
    TrackingEventName,
    VastPluginOptions,
    VideoEventNames,
} from './types';

const Plugin = videojs.getPlugin('plugin');

export type AdState =
    | 'loading'
    | 'playing'
    | 'paused'
    | 'skipped'
    | 'completed'
    | 'closed'
    | 'error'
    | 'timeout';

// @ts-expect-error - TODO: Poor videojs typing
export class VastPlugin extends Plugin<VastPluginOptions> {
    private loader: VASTLoader;
    private vast: VAST | null = null;
    private currentAd: AdData | null = null;
    private adState: AdState = 'loading';
    private skipTimer: number | null = null;
    private canSkip = true;
    private firedEvents: Set<string> = new Set();
    private timeout: number | null = null;
    private timeoutTimer: NodeJS.Timeout | null = null;

    constructor(
        private readonly player: Player,
        private options: VastPluginOptions
    ) {
        super(player, options);
        this.loader = new VASTLoader(options.url);
        this.timeout = this.options.timeout ?? DEFAULT_TIMEOUT;
        this.init({ timeout: this.timeout });
    }

    /*
    --------- Lifecycle methods ---------
    */

    private async init({ timeout }: { timeout?: number | null } = {}) {
        this.log('Initializing VastPlugin');
        if (timeout) {
            const abortController = new AbortController();
            const signal = abortController.signal;
            this.startTimeoutMonitoring(abortController);
            try {
                await this.runWithAbort(this.loadVast, signal);
                await this.runWithAbort(this.attachPlayerEvents, signal);
            } finally {
                this.clearTimeoutMonitoring();
            }
        } else {
            await this.loadVast();
            await this.attachPlayerEvents();
        }
    }

    private async loadVast() {
        try {
            this.log('Loading VAST from:', this.options.url);

            const vastList = await this.loader.load();
            this.log('VAST file loaded successfully:', vastList);

            if (vastList.length === 0) {
                this.log('No VAST documents found in VAST file');
                throw new Error('No VAST documents found in VAST file');
            } else if (vastList.length > 1) {
                this.log(
                    'VAST file contains multiple VAST documents, using the first one'
                );

                this.vast = vastList[0];
            } else {
                this.vast = vastList[0];
            }

            this.log('VAST loaded successfully:', this.vast);
            this.log('Ad state:', this.adState);
            this.log('Ads:', this.vast?.ads);
        } catch (error) {
            this.handleAdError(new Error('VAST loading error'));
        }
    }

    private async onPlayerReady() {
        if (!this.vast || this.vast.ads.length === 0) {
            this.log('No ads available to play');
            return;
        }

        // Get the first ad with a linear creative
        const ad = this.findLinearAd();
        if (!ad) {
            this.log('No linear ads found');
            this.options.onAdError?.(new Error('No linear ads found'));
            return;
        }

        this.currentAd = ad;
        this.options.onAdLoaded?.(ad);
        this.adState = 'loading';

        this.log('Starting ad playback with duration:', ad.duration);
        this.log(
            'Tracking events available:',
            Array.from(ad.trackingEvents.keys())
        );

        // Set up ad content
        await this.setupAdContent();

        // Initialize tracking
        // TODO : Impression may trigger too early here, we should probably wait for the player to be current page
        this.trackImpression();
        this.firedEvents.clear();

        // Notify on extension not supported
        if (ad.creative.extensions.length > 0 && this.options.debug) {
            this.log(
                'VAST ad contains extensions, that are not supported',
                ad.creative.extensions
            );
        }
    }

    private setupAdContent() {
        if (!this.currentAd) return;

        const { mediaFile } = this.currentAd;

        // Set the ad video source
        this.player.src({
            type: mediaFile.type || 'video/mp4',
            src: mediaFile.uri,
        });

        // Set up skip functionality
        if (this.currentAd.skipOffset) {
            this.setupSkipTimer();
        }
    }

    private _startAd() {
        // Fire ad start callback
        this.options.onAdStart?.(this.currentAd!);
        this.adState = 'playing';
        this.player.play();
    }

    private async attachPlayerEvents() {
        this.player.ready(async () => {
            if (this.options.autoplay !== false) {
                await this.onPlayerReady();
            }
        });

        // Handle ad-specific events
        this.player.on('timeupdate', () => {
            if (this.currentAd) {
                this.handleAdTimeUpdate();
            }
        });

        this.player.on('play', () => {
            if (this.currentAd) {
                if (this.adState === 'paused') {
                    this.fireEvent('resume');
                    this.adState = 'playing';
                } else {
                    this.fireEvent('start');
                }
            }
        });

        this.player.on('ended', () => {
            if (this.currentAd) {
                this.handleAdEnd();
            }
        });

        this.player.on('error', () => {
            if (this.currentAd) {
                this.handleAdError(new Error('Video playback error'));
            }
        });

        this.player.on('canplay', () => {
            if (this.currentAd) {
                this.fireEvent('loaded');
            }
        });

        this.player.on('pause', () => {
            if (this.currentAd) {
                this.fireEvent('pause');
                this.adState = 'paused';
            }
        });

        this.player.on('mute', () => {
            if (this.currentAd) {
                this.fireEvent('mute');
            }
        });

        this.player.on('unmute', () => {
            if (this.currentAd) {
                this.fireEvent('unmute');
            }
        });
    }

    private cleanup() {
        this.currentAd = null;
        if (this.skipTimer) {
            clearTimeout(this.skipTimer);
            this.skipTimer = null;
        }
        this.clearTimeoutMonitoring();
        this.firedEvents.clear();
    }

    private _dispose(): void {
        this.cleanup();
        super.dispose();
    }

    private handleAdEnd() {
        this.log('Ad ended');
        this.adState = 'completed';
        this.cleanup();
        this.options.onAdEnd?.();
    }

    private _exitHandler(): void {
        this.log('Ad exited');
        this.fireEvent('closeLinear');
        this.adState = 'closed';
        this.cleanup();
    }

    /*
    --------- Event Tracking methods ---------
    */

    private fireTrackingPixel(url: string) {
        const img = new Image();
        img.src = url;
    }

    private fireEvent(eventName: TrackingEventName) {
        if (!this.currentAd) return;

        this.log(`Firing ${eventName} event`);
        this.options.onEvent?.(eventName);
        const urls = this.currentAd.trackingEvents.get(eventName);
        if (urls) {
            urls.forEach((url) => this.fireTrackingPixel(url));
            this.firedEvents.add(eventName);
        }
    }

    private trackImpression() {
        if (!this.currentAd) return;
        this.log('Firing impression tracking');
        this.currentAd.impressionUrls.forEach((url) => {
            this.fireTrackingPixel(url);
        });
    }

    private handleAdTimeUpdate() {
        if (!this.currentAd) return;

        const currentTime = this.player.currentTime();
        if (currentTime === undefined) return;
        const duration = this.currentAd.duration;
        // Check for progress events
        this.checkProgressEvents(currentTime, duration);
    }

    private checkProgressEvents(currentTime: number, duration: number) {
        if (!this.currentAd) return;

        const { trackingEvents } = this.currentAd;

        PROGRESS_THRESHOLDS.forEach((threshold, eventName) => {
            // TODO : make sure we are reading urls only once
            const urls = trackingEvents.get(eventName);
            if (
                urls &&
                this.shouldFireEvent(eventName, currentTime, duration)
            ) {
                // Check if event has already been fired
                if (!this.firedEvents.has(eventName)) {
                    this.fireEvent(eventName);
                }
            }
        });
    }

    private shouldFireEvent(
        eventName: ProgressEventName,
        currentTime: number,
        duration: number
    ): boolean {
        const eventTime = PROGRESS_THRESHOLDS.get(eventName)! * duration;

        // For complete event, fire when video is near the end
        if (eventName === 'complete') {
            return currentTime >= duration * 0.95; // Fire at 95% of duration
        }

        // For other events, fire when we pass the threshold with a small tolerance
        const tolerance = 0.5; // 500ms tolerance
        return (
            currentTime >= eventTime - tolerance &&
            currentTime <= eventTime + tolerance
        );
    }

    /*
    --------- ClickThrough handling methods ---------
    */

    private fireClickTracking() {
        if (!this.currentAd?.clickTrackingUrls) return;

        this.log('Firing click tracking');
        this.currentAd.clickTrackingUrls.forEach((url) => {
            this.fireTrackingPixel(url);
        });
    }

    private _clickHandler(): void {
        if (this.currentAd?.clickThroughUrl) {
            this.fireClickTracking();
            window.open(this.currentAd.clickThroughUrl, '_blank');
            this.options.onAdClick?.(this.currentAd.clickThroughUrl);
        }
    }

    /*
    --------- Skip handling methods ---------
    */

    private setupSkipTimer() {
        if (!this.currentAd?.skipOffset) {
            this.canSkip = true;
            return;
        }

        this.canSkip = false;
        const skipTime = this.currentAd.skipOffset;
        this.skipTimer = window.setTimeout(() => {
            this.enableSkip();
        }, skipTime * 1000);
    }

    private enableSkip() {
        this.log('Ad can now be skipped');
        this.canSkip = true;
    }

    private skipAd() {
        if (!this.currentAd) return;

        this.log('Ad skipped');
        this.adState = 'skipped';
        this.fireEvent('skip');
        this.cleanup();
    }

    private _skipHandler(): void {
        if (this.currentAd && this.currentAd.skipOffset && this.canSkip) {
            this.skipAd();
        }
    }

    /*
    --------- Mute handling methods ---------
    */

    private _muteHandler(): void {
        this.fireEvent('mute');
    }

    private _unmuteHandler(): void {
        this.fireEvent('unmute');
    }

    /*
    --------- Error handling methods ---------
    */

    // TODO : should implement a mecanism to cache errors that arrive before VAST is loaded and error URIs are available
    private handleAdError(error: Error, errorCode?: number) {
        this.log('VAST error:', errorCode, error.message);
        this.adState = 'error';
        this.fireVastError(errorCode, error.message);
        this.options.onAdError?.(error);
        this.cleanup();
    }

    private fireVastError(errorCode?: number, message?: string) {
        // Fire VAST error URI if available
        this.log('Firing VAST error URI:', this.currentAd?.errors);
        if (this.currentAd?.errors) {
            this.currentAd.errors.forEach((error) => {
                this.fireErrorUri(error, errorCode, message);
            });
        }
    }

    private fireErrorUri(
        errorUrl: string,
        errorCode: number = 900,
        message: string = ''
    ) {
        try {
            // Replace VAST error URI parameters
            const url = errorUrl
                .replace(/\[ERRORCODE\]/g, errorCode.toString())
                .replace(/\[ERROR\]/g, encodeURIComponent(message))
                .replace(/\[TIMESTAMP\]/g, Date.now().toString());

            this.log('Firing VAST error URI:', url);

            // Fire error URI asynchronously to avoid blocking
            this.fireTrackingPixel(url);
        } catch (error) {
            this.log('Error firing VAST error URI:', error);
        }
    }
    /*
    --------- Timeout handling methods ---------
    */

    private startTimeoutMonitoring(controller: AbortController) {
        if (this.timeout === null || this.timeout === undefined) return;

        this.log('Starting timeout monitoring for', this.timeout, 'ms');

        this.timeoutTimer = setTimeout(() => {
            controller.abort();
            this.handleTimeout();
        }, this.timeout);
    }

    private clearTimeoutMonitoring() {
        if (this.timeoutTimer) {
            clearTimeout(this.timeoutTimer);
            this.timeoutTimer = null;
            this.log('Timeout monitoring cleared');
        }
    }

    private handleTimeout() {
        this.log('Ad timeout occurred');
        this.handleAdError(new Error('VAST timeout'), 301);
    }

    /*
    --------- Public API methods ---------
    */

    public getAdState(): AdState {
        return this.adState;
    }

    public isAdActive(): boolean {
        return !!this.currentAd;
    }

    public dispose(): void {
        this._dispose();
    }

    public startAd(): void {
        this._startAd();
    }

    public clickHandler(): void {
        this._clickHandler();
    }

    public skipHandler(): void {
        this._skipHandler();
    }

    public exitHandler(): void {
        this._exitHandler();
    }

    public muteHandler(): void {
        this._muteHandler();
    }

    public unmuteHandler(): void {
        this._unmuteHandler();
    }

    /*
    --------- Utility methods ---------
    */

    private async runWithAbort(fn: () => Promise<void>, signal: AbortSignal) {
        return new Promise((resolve, reject) => {
            if (signal.aborted) {
                reject('Aborted before execution');
                return;
            }

            const onAbort = () => {
                reject('Aborted during execution');
            };
            signal.addEventListener('abort', onAbort);

            // Bind the function to maintain the correct 'this' context
            const boundFn = fn.bind(this);

            boundFn()
                .then(resolve)
                .catch(reject)
                .finally(() => {
                    signal.removeEventListener('abort', onAbort);
                });
        });
    }

    private log(...message: unknown[]) {
        if (this.options.debug) {
            videojs.log('VastPlugin', ...message);
        }
    }

    private findLinearAd(): AdData | null {
        if (!this.vast) return null;

        const ads = Array.from(this.vast.ads);
        for (const ad of ads) {
            if (!ad) continue;
            const creatives = Array.from(ad.creatives);
            for (const creative of creatives) {
                if (!creative) continue;
                if (creative.linear && creative.linear.mediaFiles.length > 0) {
                    const mediaFile = this.selectBestMediaFile(
                        creative.linear.mediaFiles
                    );
                    if (!ad || !creative.linear) continue;
                    if (mediaFile) {
                        const adData = {
                            linear: creative.linear,
                            creative,
                            ad,
                            mediaFile,
                            duration: creative.linear.duration || 0,
                            skipOffset:
                                creative.linear.skipoffset?.value || null,
                            clickThroughUrl: this.getClickThroughUrl(
                                creative.linear.videoClicks
                            ),
                            clickTrackingUrls: this.getClickTrackingUrls(
                                creative.linear.videoClicks
                            ),
                            impressionUrls: this.getImpressionUrls(ad),
                            trackingEvents: this.getTrackingEvents(
                                creative.linear.trackingEvents
                            ),
                            adTitle: (ad as InLine).adTitle || null,
                            advertiser: (ad as InLine).advertiser || null,
                            errors: [
                                ...(this.vast.errors as string[]),
                                ...(ad.errors as string[]),
                            ],
                        };
                        return adData;
                    }
                }
            }
        }
        return null;
    }

    private selectBestMediaFile(mediaFiles: MediaFile[]): MediaFile | null {
        // Simple selection logic - prefer progressive delivery, then streaming
        // In a real implementation, you'd want more sophisticated selection
        const progressive = mediaFiles.find(
            (mf) => mf.delivery === 'progressive'
        );
        if (progressive) return progressive;

        const streaming = mediaFiles.find((mf) => mf.delivery === 'streaming');
        if (streaming) return streaming;

        return mediaFiles[0] || null;
    }

    private getClickThroughUrl(videoClicks: VideoClicks | null): string | null {
        if (!videoClicks) return null;
        const clickUri = videoClicks.clickThrough?.uri || null;
        return clickUri;
    }

    private getClickTrackingUrls(videoClicks: VideoClicks | null): string[] {
        if (!videoClicks) return [];
        return videoClicks.clickTrackings
            .map((click: Click) => click.uri)
            .filter((uri: string | null): uri is string => uri !== null);
    }

    private getImpressionUrls(ad: AbstractAd): string[] {
        return ad.impressions
            .map((impression: Impression) => impression.uri)
            .filter((uri: string | null): uri is string => uri !== null);
    }

    private getTrackingEvents(
        trackingEvents: TrackingEvents | null
    ): Map<TrackingEventName, string[]> {
        const events = new Map<TrackingEventName, string[]>();
        if (!trackingEvents) return events;

        this.log('Tracking events:', trackingEvents);

        for (const eventName of trackingEvents.types) {
            if (
                ![...VideoEventNames, ...ProgressEventNames].includes(
                    eventName as TrackingEventName
                )
            ) {
                this.log('Unsupported tracking event:', eventName);
                continue;
            }
            events.set(
                eventName as TrackingEventName,
                trackingEvents
                    .get(eventName)
                    .map((event: TrackingEvent) => event.uri)
                    .filter((uri: string | null): uri is string => uri !== null)
            );
        }
        return events;
    }
}
